import { React } from 'Imports';
import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDatabase, faCircleXmark } from '@fortawesome/pro-regular-svg-icons';

import { IAgreementsServiceInjectedProps, AgreementsService } from '$State/AgreementsFreezerService';
import { IConfigServiceInjectedProps, ConfigService } from '$State/ConfigFreezerService';
import { ICriticalSpeedServiceInjectedProps, CriticalSpeedService } from '$State/CriticalSpeedFreezerService';
import { IDashboardServiceInjectedProps, DashboardService } from '$State/DashboardFreezerService';
import { IDeviceAssociationServiceInjectedProps, DeviceAssociationService } from '$State/DeviceAssociationFreezerService';
import { IDriverNotificationServiceInjectedProps, DriverNotificationService } from '$State/DriverNotificationFreezerService';
import {
    IEventNotificationSettingsServiceInjectedProps,
    EventNotificationSettingsService,
} from '$State/EventNotificationSettingsFreezerService';
import { IFleetSettingsServiceInjectedProps, FleetSettingsService } from '$State/FleetSettingsFreezerService';
import { IInstallersServiceInjectedProps, InstallersService } from '$State/InstallersFreezerService';
import { IIntegrationPartnerDataInjectedProps, IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { IPricingPlanServiceInjectedProps, PricingPlanService } from '$State/PricingPlanFreezerService';
import { IUserManagementServiceInjectedProps, UserManagementService } from '$State/UserManagementFreezerService';
import { IUserRoleServiceInjectedProps, UserRoleService } from '$State/UserRoleFreezerService';
import {
    IVideoEventFilterServiceMultiselectInjectedProps,
    VideoEventFilterServiceMultiselect,
} from '$State/VideoEventFilterFreezerServiceMultiselect';
import { IVideoEventServiceInjectedProps, VideoEventService } from '$State/VideoEventFreezerService';
import { IVideoRecallFilterServiceInjectedProps, VideoRecallFilterService } from '$State/VideoRecallEventFilterFreezerService';
import { IVideoRecallServiceInjectedProps, VideoRecallService } from '$State/VideoRecallFreezerService';
import MultiGroupSelect, { Option } from './components/MultiGroupSelect';
import { GroupDto } from '$Generated/api';

const styles = require('./StateViewer.scss') as {
    viewStateButton: string;
    debugStateContainer: string;
    row: string;
    doubleColumn: string;
    header: string;
    closeDebugStateContainerButton: string;
    content: string;
    column: string;
    contextMenu: string;
    buttonSelected: string;
    contextStateContent: string;
    updateGroupsButton: string;
    jsonKey: string;
    jsonString: string;
    jsonNumber: string;
    jsonBoolean: string;
    jsonNull: string;
};

const CONTEXT_TYPES = {
    agreementsService: {
        id: 'agreementsService',
        displayName: 'Agreements Service',
        serviceKey: 'agreements',
    },
    configService: {
        id: 'configService',
        displayName: 'Config Service',
        serviceKey: 'config',
    },
    criticalSpeedService: {
        id: 'criticalSpeedService',
        displayName: 'Critical Speed Service',
        serviceKey: 'CriticalSpeedSettings',
    },
    dashboardService: {
        id: 'dashboardService',
        displayName: 'Dashboard Service',
        serviceKey: 'dashboard',
    },
    deviceAssociationService: {
        id: 'deviceAssociationService',
        displayName: 'Device Association Service',
        serviceKey: 'deviceAssociation',
    },
    driverNotificationService: {
        id: 'driverNotificationService',
        displayName: 'Driver Notification Service',
        serviceKey: 'driverNotifications',
    },
    eventNotificationSettingsService: {
        id: 'eventNotificationSettingsService',
        displayName: 'Event Notification Settings Service',
        serviceKey: 'eventNotificationSettings',
    },
    fleetSettingsService: {
        id: 'fleetSettingsService',
        displayName: 'Fleet Settings Service',
        serviceKey: 'FleetSettings',
    },
    installersService: {
        id: 'installersService',
        displayName: 'Installers Service',
        serviceKey: 'installers',
    },
    integrationPartnerDataService: {
        id: 'integrationPartnerDataService',
        displayName: 'Integration Partner Data Service',
        serviceKey: 'integrationPartnerData',
    },
    pricingPlanService: {
        id: 'pricingPlanService',
        displayName: 'Pricing Plan Service',
        serviceKey: 'pricingPlan',
    },
    userManagementService: {
        id: 'userManagementService',
        displayName: 'User Management Service',
        serviceKey: 'userManagement',
    },
    userRoleService: {
        id: 'userRoleService',
        displayName: 'User Role Service',
        serviceKey: 'userRoles',
    },
    videoEventFilterServiceMultiselect: {
        id: 'videoEventFilterServiceMultiselect',
        displayName: 'Video Event Filter Service Multiselect',
        serviceKey: 'videoEventFilterMultiselect',
    },
    videoEventService: {
        id: 'videoEventService',
        displayName: 'Video Event Service',
        serviceKey: 'videoEvents',
    },
    videoRecallFilterService: {
        id: 'videoRecallFilterService',
        displayName: 'Video Recall Filter Service',
        serviceKey: 'videoRecallEventFilter',
    },
    videoRecallService: {
        id: 'videoRecallService',
        displayName: 'Video Recall Service',
        serviceKey: 'videoRecall',
    },
} as const;

type ContextType = keyof typeof CONTEXT_TYPES;

// Session storage utilities for showStateViewer
const SESSION_STORAGE_KEY = 'showStateViewer';

const initializeShowStateViewer = (isStandalone: boolean): boolean => {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing !== null) {
        return existing === 'true';
    }
    // Default: true for standalone, false for integrated
    const defaultValue = isStandalone;
    sessionStorage.setItem(SESSION_STORAGE_KEY, String(defaultValue));
    return defaultValue;
};

const getShowStateViewer = (): boolean => {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
};

type StateViewerProps = IUserManagementServiceInjectedProps &
    IAgreementsServiceInjectedProps &
    IConfigServiceInjectedProps &
    ICriticalSpeedServiceInjectedProps &
    IDashboardServiceInjectedProps &
    IDeviceAssociationServiceInjectedProps &
    IDriverNotificationServiceInjectedProps &
    IEventNotificationSettingsServiceInjectedProps &
    IFleetSettingsServiceInjectedProps &
    IInstallersServiceInjectedProps &
    IIntegrationPartnerDataInjectedProps &
    IPricingPlanServiceInjectedProps &
    IUserRoleServiceInjectedProps &
    IVideoEventFilterServiceMultiselectInjectedProps &
    IVideoEventServiceInjectedProps &
    IVideoRecallFilterServiceInjectedProps &
    IVideoRecallServiceInjectedProps;

const _StateViewer = (props: StateViewerProps) => {
    if (props.config.isDeployEnvironmentProd()) {
        return null;
    }

    const isStandalone = props.config.isRunningStandAlone();
    const showStateViewer = initializeShowStateViewer(isStandalone);

    if (!showStateViewer) {
        return null;
    }

    const [selectedContext, setSelectedContext] = useState<ContextType>('userManagementService');
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [companyGroups, setCompanyGroups] = useState<string[]>([]);
    const contentRef = useRef<HTMLDivElement>(null);
    const integration = props.config.getIntegrationPlatform().toLowerCase();

    const originalGroupIds = (props.integrationPartnerData.getGroupsFilter() || '').split(',').filter(Boolean);
    const isGroupsChanged =
        originalGroupIds.length !== companyGroups.length ||
        originalGroupIds.some((id) => !companyGroups.includes(id)) ||
        companyGroups.some((id) => !originalGroupIds.includes(id ?? ''));

    const handleContextMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        const selectedContext = e.currentTarget.name;
        setSelectedContext(selectedContext as ContextType);
    };

    const getServiceState = (contextType: ContextType, dataPropertyGetter?: string) => {
        const serviceKey = CONTEXT_TYPES[contextType]?.serviceKey;
        if (!serviceKey || !(serviceKey in props)) {
            return null;
        }

        if (dataPropertyGetter !== undefined) {
            return (props as any)[serviceKey]?.freezer.get()?.[dataPropertyGetter];
        } else {
            return (props as any)[serviceKey]?.freezer.get();
        }
    };

    const getCompanyGroups = (): GroupDto[] => {
        return props.integrationPartnerData.getCompanyGroups();
    };

    const handleCompanyGroupChange = (selectedOptions: string[]) => {
        setCompanyGroups(selectedOptions);
    };

    const handleUpdateGroupState = () => {
        const selectedGroupIds = companyGroups.join(',');
        props.integrationPartnerData.setGroupsFilter(selectedGroupIds);
    };

    const getCurrentGroupFilterOptions = (): string[] => {
        const currentGroupFilterOptions = props.integrationPartnerData.getGroupsFilter();
        return currentGroupFilterOptions ? currentGroupFilterOptions.split(',').filter(Boolean) : [];
    };

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [selectedContext]);

    const renderJsonValue = (value: any, key?: string): React.ReactNode => {
        if (value === null) {
            return <span className={styles.jsonNull}>null</span>;
        }

        if (typeof value === 'boolean') {
            return <span className={styles.jsonBoolean}>{String(value)}</span>;
        }

        if (typeof value === 'number') {
            return <span className={styles.jsonNumber}>{value}</span>;
        }

        if (typeof value === 'string') {
            return <span className={styles.jsonString}>"{value}"</span>;
        }

        if (Array.isArray(value)) {
            if (value.length === 0) {
                return <>[]</>;
            }
            return (
                <>
                    [<div style={{ marginLeft: '20px' }}>
                        {value.map((item, index) => (
                            <div key={index}>
                                {renderJsonValue(item)}
                                {index < value.length - 1 ? ',' : ''}
                            </div>
                        ))}
                    </div>]
                </>
            );
        }

        if (typeof value === 'object') {
            const entries = Object.entries(value);
            if (entries.length === 0) {
                return <>{'{}'}</>;
            }
            return (
                <>
                    {'{'}<div style={{ marginLeft: '20px' }}>
                        {entries.map(([objKey, objValue], index) => (
                            <div key={objKey}>
                                <span className={styles.jsonKey}>"{objKey}"</span>: {renderJsonValue(objValue, objKey)}
                                {index < entries.length - 1 ? ',' : ''}
                            </div>
                        ))}
                    </div>{'}'}
                </>
            );
        }

        return String(value);
    };

    return (
        <div>
            {isCollapsed && <FontAwesomeIcon icon={faDatabase} className={styles.viewStateButton} onClick={() => setIsCollapsed(false)} />}
            {!isCollapsed && (
                <div className={styles.debugStateContainer}>
                    <div className={styles.row}>
                        <div className={styles.doubleColumn}>
                            <div className={styles.header}>
                                <div style={{ display: 'inline-block', alignItems: 'center', marginLeft: '10px' }}>
                                    <p>Debug State</p>
                                    {integration === 'geotab' && (
                                        <>
                                            <div style={{ display: 'inline-block', verticalAlign: 'middle', margin: '15px' }}>
                                                <label style={{ color: 'limegreen', marginRight: '10px' }}>Select Group Filters:</label>
                                                <MultiGroupSelect
                                                    options={getCompanyGroups()}
                                                    placeholder="Choose group filter(s)..."
                                                    onChange={handleCompanyGroupChange}
                                                    selected={companyGroups}
                                                />
                                            </div>
                                            <button
                                                disabled={!isGroupsChanged}
                                                className={styles.updateGroupsButton}
                                                onClick={handleUpdateGroupState}
                                            >
                                                Update Group(s)
                                            </button>
                                        </>
                                    )}
                                </div>

                                <FontAwesomeIcon
                                    icon={faCircleXmark}
                                    onClick={() => {
                                        if (integration === 'geotab') {
                                            setCompanyGroups(getCurrentGroupFilterOptions());
                                        }
                                        setIsCollapsed(true);
                                    }}
                                    className={styles.closeDebugStateContainerButton}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={styles.content}>
                        <div className={styles.row}>
                            <div className={styles.column}>
                                <div className={styles.contextMenu}>
                                    {Object.entries(CONTEXT_TYPES).map(([key, value]) => (
                                        <button
                                            key={key}
                                            name={key}
                                            onClick={(e) => handleContextMenuClick(e)}
                                            className={selectedContext === key ? styles.buttonSelected : ''}
                                        >
                                            {value.displayName}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.column}>
                                <div className={styles.contextStateContent} ref={contentRef}>
                                    {selectedContext && CONTEXT_TYPES[selectedContext]?.serviceKey && (
                                        <pre>
                                            {renderJsonValue(getServiceState(selectedContext))}
                                        </pre>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export const StateViewer = UserManagementService.inject(
    AgreementsService.inject(
        ConfigService.inject(
            CriticalSpeedService.inject(
                DashboardService.inject(
                    DeviceAssociationService.inject(
                        DriverNotificationService.inject(
                            EventNotificationSettingsService.inject(
                                FleetSettingsService.inject(
                                    InstallersService.inject(
                                        IntegrationPartnerDataService.inject(
                                            PricingPlanService.inject(
                                                UserRoleService.inject(
                                                    VideoEventFilterServiceMultiselect.inject(
                                                        VideoEventService.inject(
                                                            VideoRecallFilterService.inject(VideoRecallService.inject(_StateViewer)),
                                                        ),
                                                    ),
                                                ),
                                            ),
                                        ),
                                    ),
                                ),
                            ),
                        ),
                    ),
                ),
            ),
        ),
    ),
);
